(() => {
  'use strict';

  const SELECTOR = {
    section: '[data-c1-section]',
    localNav: '[data-c1-local-nav]',
    navLink: '[data-c1-nav-link]',
    rail: '[data-c1-chapter-rail]',
    backTop: '[data-c1-backtop]',
    slider: '[data-c1-slider]',
    tabs: '[data-c1-tabs]',
    roi: '[data-c1-roi]',
    product: '[data-c1-product]',
    reveal: '[data-c1-reveal]'
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const safeId = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const getScrollOffset = () => {
    const nav = document.querySelector(SELECTOR.localNav);
    if (!nav) return 16;
    const navHeight = nav.getBoundingClientRect().height || 0;
    const wrapper = nav.closest('.shopify-section');
    const top = wrapper ? parseFloat(getComputedStyle(wrapper).top || 0) : 0;
    return navHeight + top + 12;
  };

  const scrollToTarget = (target) => {
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - getScrollOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const bindGlobalAnchors = () => {
    if (document.documentElement.dataset.c1AnchorBound === 'true') return;
    document.documentElement.dataset.c1AnchorBound = 'true';

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"], button[data-c1-target]');
      if (!link) return;
      const rawTarget = link.getAttribute('href') || link.dataset.c1Target;
      if (!rawTarget || rawTarget === '#') return;
      const targetId = rawTarget.replace(/^#/, '');
      const target = document.getElementById(targetId);
      if (!target) return;
      event.preventDefault();
      scrollToTarget(target);
      if (link.tagName === 'A') history.replaceState(null, '', `#${targetId}`);
    });
  };

  const buildNavigation = () => {
    const chapters = [...document.querySelectorAll('[data-c1-chapter="true"][id]')];
    const rail = document.querySelector(SELECTOR.rail);
    const navLinks = [...document.querySelectorAll(SELECTOR.navLink)];

    if (rail) {
      rail.innerHTML = '';
      chapters.forEach((section, index) => {
        const sectionLabel = document.querySelector('[data-c1-shell]')?.dataset.sectionLabel || '';
        const label = section.dataset.c1ChapterLabel || section.querySelector('h2, h1')?.textContent?.trim() || `${sectionLabel} ${index + 1}`;
        const link = document.createElement('a');
        link.className = 'c1l-rail-link';
        link.href = `#${section.id}`;
        link.dataset.c1RailLink = section.id;
        link.innerHTML = `<span aria-hidden="true"></span><span>${label}</span>`;
        link.setAttribute('aria-label', `${String(index + 1).padStart(2, '0')} — ${label}`);
        rail.appendChild(link);
      });
      rail.hidden = chapters.length < 2;
    }

    if (window.c1NavigationObserver) window.c1NavigationObserver.disconnect();

    if (!chapters.length || (!rail && document.querySelector(SELECTOR.localNav))) return;

    const setActive = (id) => {
      document.querySelectorAll('[data-c1-rail-link]').forEach((link) => {
        const active = link.dataset.c1RailLink === id;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      navLinks.forEach((link) => {
        const target = (link.getAttribute('href') || '').replace(/^#/, '');
        const active = target === id;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
    };

    window.c1NavigationObserver = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-18% 0px -66% 0px', threshold: [0, 0.05, 0.2, 0.5] });

    chapters.forEach((section) => window.c1NavigationObserver.observe(section));
  };

  const initBackToTop = () => {
    const button = document.querySelector(SELECTOR.backTop);
    if (!button || button.dataset.c1Initialized === 'true') return;
    button.dataset.c1Initialized = 'true';

    const toggle = () => button.classList.toggle('is-visible', window.scrollY > Math.max(650, window.innerHeight * 0.85));
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' }));
  };

  const initReveals = (root = document) => {
    document.documentElement.classList.add('c1l-motion-ready');
    const items = [...root.querySelectorAll(SELECTOR.reveal)].filter((item) => item.dataset.c1RevealInitialized !== 'true');
    if (!items.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, io) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item, index) => {
      item.dataset.c1RevealInitialized = 'true';
      item.style.transitionDelay = `${Math.min(index % 6, 5) * 55}ms`;
      observer.observe(item);
    });
  };

  const initSlider = (slider) => {
    if (slider.dataset.c1Initialized === 'true') return;
    slider.dataset.c1Initialized = 'true';

    const track = slider.querySelector('[data-c1-slider-track]');
    const slides = [...slider.querySelectorAll('[data-c1-slide]')];
    const prev = slider.querySelector('[data-c1-prev]');
    const next = slider.querySelector('[data-c1-next]');
    const dots = slider.querySelector('[data-c1-dots]');
    if (!track || !slides.length) return;

    let index = 0;
    let timer = null;
    let scrollTimer = null;

    const slideIndexFromScroll = () => {
      const trackRect = track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, slideIndex) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const currentDistance = Math.abs(center - slideCenter);
        if (currentDistance < distance) {
          distance = currentDistance;
          nearest = slideIndex;
        }
      });
      return nearest;
    };

    const update = (newIndex = slideIndexFromScroll()) => {
      index = Math.max(0, Math.min(slides.length - 1, newIndex));
      if (prev) prev.disabled = index === 0 && slider.dataset.loop !== 'true';
      if (next) next.disabled = index === slides.length - 1 && slider.dataset.loop !== 'true';
      if (dots) {
        dots.querySelectorAll('button').forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    };

    const goTo = (newIndex) => {
      if (slider.dataset.loop === 'true') {
        if (newIndex < 0) newIndex = slides.length - 1;
        if (newIndex >= slides.length) newIndex = 0;
      }
      newIndex = Math.max(0, Math.min(slides.length - 1, newIndex));
      const target = slides[newIndex];
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      update(newIndex);
    };

    if (dots) {
      dots.innerHTML = '';
      slides.forEach((_, dotIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'c1l-slider-dot';
        const label = slider.dataset.goToSlideLabel || 'Go to slide [number]';
        dot.setAttribute('aria-label', label.replace('[number]', String(dotIndex + 1)));
        dot.addEventListener('click', () => goTo(dotIndex));
        dots.appendChild(dot);
      });
    }

    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));

    track.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => update(), 80);
    }, { passive: true });

    const stopAutoplay = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (slider.dataset.autoplay !== 'true' || prefersReducedMotion() || slides.length < 2) return;
      const interval = Math.max(2500, Number(slider.dataset.interval || 5000));
      timer = window.setInterval(() => goTo(index + 1), interval);
    };

    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', startAutoplay);
    slider.addEventListener('focusin', stopAutoplay);
    slider.addEventListener('focusout', startAutoplay);
    document.addEventListener('visibilitychange', () => document.hidden ? stopAutoplay() : startAutoplay());
    window.addEventListener('resize', () => update(index), { passive: true });

    update(0);
    startAutoplay();
  };

  const initTabs = (tabs) => {
    if (tabs.dataset.c1Initialized === 'true') return;
    tabs.dataset.c1Initialized = 'true';

    const buttons = [...tabs.querySelectorAll('[role="tab"]')];
    const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];
    if (!buttons.length) return;

    const activate = (button, moveFocus = false, scrollTab = true) => {
      const panelId = button.getAttribute('aria-controls');
      buttons.forEach((item) => {
        const active = item === button;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.id === panelId;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      if (moveFocus) button.focus({ preventScroll: true });
      const list = button.closest('[role="tablist"]');
      if (scrollTab && list && list.scrollWidth > list.clientWidth) {
        const buttonRect = button.getBoundingClientRect();
        const listRect = list.getBoundingClientRect();
        list.scrollBy({ left: buttonRect.left + buttonRect.width / 2 - listRect.left - listRect.width / 2, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button));
      button.addEventListener('keydown', (event) => {
        let targetIndex = null;
        const isRtl = getComputedStyle(tabs).direction === 'rtl';
        if (event.key === 'ArrowDown') targetIndex = (index + 1) % buttons.length;
        if (event.key === 'ArrowUp') targetIndex = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'ArrowRight') targetIndex = (index + (isRtl ? -1 : 1) + buttons.length) % buttons.length;
        if (event.key === 'ArrowLeft') targetIndex = (index + (isRtl ? 1 : -1) + buttons.length) % buttons.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = buttons.length - 1;
        if (targetIndex === null) return;
        event.preventDefault();
        activate(buttons[targetIndex], true);
      });
    });

    const selected = buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0];
    activate(selected, false, false);
  };

  const formatCurrency = (value, symbol = '$', decimals = 0) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${symbol}${safeValue.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const initROI = (roi) => {
    if (roi.dataset.c1Initialized === 'true') return;
    roi.dataset.c1Initialized = 'true';

    const jobs = roi.querySelector('[data-roi-jobs]');
    const days = roi.querySelector('[data-roi-days]');
    const profit = roi.querySelector('[data-roi-profit]');
    const overhead = roi.querySelector('[data-roi-overhead]');
    const price = Number(roi.dataset.price || 0);
    const currency = roi.dataset.currency || '$';

    const jobsOutput = roi.querySelector('[data-roi-jobs-output]');
    const daysOutput = roi.querySelector('[data-roi-days-output]');
    const profitOutput = roi.querySelector('[data-roi-profit-output]');
    const overheadOutput = roi.querySelector('[data-roi-overhead-output]');
    const monthlyOutput = roi.querySelector('[data-roi-monthly-output]');
    const annualOutput = roi.querySelector('[data-roi-annual-output]');
    const paybackOutput = roi.querySelector('[data-roi-payback-output]');

    if (!jobs || !days || !profit || !overhead) return;

    const calculate = () => {
      const jobsValue = Number(jobs.value || 0);
      const daysValue = Number(days.value || 0);
      const profitValue = Number(profit.value || 0);
      const overheadValue = Number(overhead.value || 0);
      const monthly = Math.max(0, jobsValue * daysValue * profitValue - overheadValue);
      const annual = monthly * 12;
      const payback = monthly > 0 ? price / monthly : 0;

      if (jobsOutput) jobsOutput.textContent = jobsValue.toLocaleString();
      if (daysOutput) daysOutput.textContent = daysValue.toLocaleString();
      if (profitOutput) profitOutput.textContent = formatCurrency(profitValue, currency, 0);
      if (overheadOutput) overheadOutput.textContent = formatCurrency(overheadValue, currency, 0);
      if (monthlyOutput) monthlyOutput.textContent = formatCurrency(monthly, currency, 0);
      if (annualOutput) annualOutput.textContent = formatCurrency(annual, currency, 0);
      const monthLabel = roi.dataset.monthLabel || 'mo.';
      if (paybackOutput) paybackOutput.textContent = monthly > 0 ? `${Math.max(0.1, payback).toFixed(payback < 1 ? 1 : 0)} ${monthLabel}` : '—';
    };

    [jobs, days, profit, overhead].forEach((input) => input.addEventListener('input', calculate));
    calculate();
  };

  const initProductGallery = (scope) => {
    const track = scope.querySelector('[data-c1-gallery-track]');
    const slides = [...scope.querySelectorAll('[data-c1-gallery-slide]')];
    const thumbs = [...scope.querySelectorAll('[data-c1-gallery-thumb]')];
    const prev = scope.querySelector('[data-c1-gallery-prev]');
    const next = scope.querySelector('[data-c1-gallery-next]');
    if (!track || !slides.length) return;

    let index = 0;
    let timer = null;

    const update = (newIndex) => {
      index = Math.max(0, Math.min(slides.length - 1, newIndex));
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    };

    const goTo = (newIndex) => {
      newIndex = Math.max(0, Math.min(slides.length - 1, newIndex));
      slides[newIndex].scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      update(newIndex);
    };

    thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => goTo(thumbIndex)));
    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    track.addEventListener('scroll', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const trackRect = track.getBoundingClientRect();
        const isRtl = getComputedStyle(track).direction === 'rtl';
        const edge = isRtl ? trackRect.right : trackRect.left;
        let current = 0;
        let distance = Number.POSITIVE_INFINITY;
        slides.forEach((slide, slideIndex) => {
          const slideRect = slide.getBoundingClientRect();
          const slideEdge = isRtl ? slideRect.right : slideRect.left;
          const nextDistance = Math.abs(slideEdge - edge);
          if (nextDistance < distance) {
            distance = nextDistance;
            current = slideIndex;
          }
        });
        update(current);
      }, 80);
    }, { passive: true });
    update(0);
  };

  const initProduct = (product) => {
    if (product.dataset.c1Initialized === 'true') return;
    product.dataset.c1Initialized = 'true';

    initProductGallery(product);

    const form = product.querySelector('[data-c1-product-form]');
    if (!form) return;

    const variantInputs = [...form.querySelectorAll('[data-c1-variant-input]')];
    const currentPrice = product.querySelector('[data-c1-current-price]');
    const comparePrice = product.querySelector('[data-c1-compare-price]');
    const addButton = form.querySelector('[data-c1-add-button]');
    const addText = addButton?.querySelector('[data-c1-add-text]');
    const status = product.querySelector('[data-c1-product-status]');
    const mobilePrice = document.querySelector(`[data-c1-mobile-price="${product.dataset.sectionId}"]`);
    const soldOutLabel = addButton?.dataset.soldOutLabel || '';
    const addLabel = addButton?.dataset.addLabel || '';

    const updateVariant = (input) => {
      if (!input) return;
      const available = input.dataset.available === 'true';
      const price = input.dataset.price || '';
      const compare = input.dataset.compare || '';
      const compareRaw = Number(input.dataset.compareRaw || 0);
      const priceRaw = Number(input.dataset.priceRaw || 0);

      if (currentPrice) currentPrice.textContent = price;
      if (mobilePrice) mobilePrice.textContent = price;
      if (comparePrice) {
        comparePrice.textContent = compare;
        comparePrice.hidden = !(compareRaw > priceRaw);
      }
      if (addButton) addButton.disabled = !available;
      if (addText) addText.textContent = available ? addLabel : soldOutLabel;
      if (status) {
        status.textContent = available ? (status.dataset.inStockLabel || '') : (status.dataset.soldOutLabel || soldOutLabel);
        status.classList.toggle('is-sold-out', !available);
      }

      document.dispatchEvent(new CustomEvent('c1:variant-change', {
        detail: { id: input.value, available, price, priceRaw }
      }));
    };

    variantInputs.forEach((input) => input.addEventListener('change', () => updateVariant(input)));
    updateVariant(variantInputs.find((input) => input.checked) || variantInputs[0]);

    form.querySelectorAll('[data-c1-qty-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const input = form.querySelector('input[name="quantity"]');
        if (!input) return;
        const delta = Number(button.dataset.c1QtyChange || 0);
        const nextValue = Math.max(Number(input.min || 1), Number(input.value || 1) + delta);
        input.value = nextValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initMobileBuyBars = () => {
    document.querySelectorAll('[data-c1-mobile-buybar]').forEach((bar) => {
      if (bar.dataset.c1Initialized === 'true') return;
      bar.dataset.c1Initialized = 'true';
      const button = bar.querySelector('[data-c1-scroll-to-form]');
      button?.addEventListener('click', () => {
        const target = document.getElementById(bar.dataset.target);
        if (target) scrollToTarget(target);
      });
    });
  };

  const initAll = (root = document) => {
    bindGlobalAnchors();
    root.querySelectorAll(SELECTOR.slider).forEach(initSlider);
    root.querySelectorAll(SELECTOR.tabs).forEach(initTabs);
    root.querySelectorAll(SELECTOR.roi).forEach(initROI);
    root.querySelectorAll(SELECTOR.product).forEach(initProduct);
    initMobileBuyBars();
    initReveals(root);
    initBackToTop();
    window.requestAnimationFrame(buildNavigation);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:reorder', () => buildNavigation());
  document.addEventListener('shopify:section:unload', () => window.requestAnimationFrame(buildNavigation));
})();
